import React, { useState, useEffect, useCallback } from 'react';
import BackButton from '@/components/BackButton';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Briefcase, UploadSimple, MagnifyingGlass, Eye, Trash, PencilSimple,
  SpinnerGap, ArrowLeft, ArrowRight, UserPlus, CheckCircle, XCircle,
  Clock, Users, FileText, FunnelSimple
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

const STATUS_CONFIG = {
  new: { color: 'bg-blue-100 text-blue-700 border-blue-200', ar: 'جديد', en: 'New' },
  reviewing: { color: 'bg-amber-100 text-amber-700 border-amber-200', ar: 'قيد المراجعة', en: 'Reviewing' },
  referred_to_dept: { color: 'bg-orange-100 text-orange-700 border-orange-200', ar: 'محال لمدير القسم', en: 'Referred to Dept' },
  interview: { color: 'bg-purple-100 text-purple-700 border-purple-200', ar: 'مرشح للمقابلة', en: 'Interview' },
  interviewed: { color: 'bg-indigo-100 text-indigo-700 border-indigo-200', ar: 'تم المقابلة', en: 'Interviewed' },
  dept_approved: { color: 'bg-teal-100 text-teal-700 border-teal-200', ar: 'معتمد من مدير القسم', en: 'Dept Approved' },
  gm_approved: { color: 'bg-cyan-100 text-cyan-700 border-cyan-200', ar: 'معتمد من المدير العام', en: 'GM Approved' },
  accepted: { color: 'bg-green-100 text-green-700 border-green-200', ar: 'مقبول', en: 'Accepted' },
  rejected: { color: 'bg-red-100 text-red-700 border-red-200', ar: 'مرفوض', en: 'Rejected' },
};

const NEXT_ACTION_MAP = {
  new: { action: 'start_review', ar: 'بدء المراجعة', en: 'Start Review' },
  reviewing: { action: 'refer_to_dept', ar: 'إحالة لمدير القسم', en: 'Refer to Dept' },
  gm_approved: { action: null, ar: 'إصدار عرض وظيفي', en: 'Generate Offer' },
};

export default function RecruitmentDashboard() {
  const { t } = useTranslation();
  const { language, isRTL } = useLanguage();
  const navigate = useNavigate();
  const direction = isRTL ? 'rtl' : 'ltr';

  const [applicants, setApplicants] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [editForm, setEditForm] = useState({ status: '', department: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [offerForm, setOfferForm] = useState({
    basic_salary: '', housing_allowance: '', transport_allowance: '', other_allowances: '',
    job_title: '', medical_care: 'تأمين صحي بعد فترة التجربة', annual_leave_days: 30,
    probation_days: 90, offer_validity_days: 7, work_location: 'الرياض', expected_start_date: ''
  });
  const [generatingOffer, setGeneratingOffer] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [showTimeline, setShowTimeline] = useState(false);
  const [timeline, setTimeline] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterDept) params.append('department', filterDept);
      if (filterStatus) params.append('status', filterStatus);

      const [appRes, deptRes, statsRes] = await Promise.all([
        axios.get(`${API}/recruitment/applicants?${params}`, { withCredentials: true }),
        axios.get(`${API}/recruitment/departments`, { withCredentials: true }),
        axios.get(`${API}/recruitment/stats`, { withCredentials: true }),
      ]);
      setApplicants(appRes.data);
      setDepartments(deptRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, filterDept, filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getDeptLabel = (val) => {
    const d = departments.find(dep => dep.value === val);
    return d ? (language === 'ar' ? d.label_ar : d.label_en) : val;
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(pdf|doc|docx)$/i)) {
      toast.error(language === 'ar' ? 'يرجى رفع ملف PDF أو Word' : 'Please upload PDF or Word file');
      return;
    }

    setUploading(true);
    setShowUploadDialog(false);
    toast.info(language === 'ar' ? 'جاري تحليل السيرة الذاتية...' : 'Analyzing CV...');

    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/recruitment/upload-cv`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });

      if (data.name_ar || data.name_en) {
        toast.success(language === 'ar'
          ? `تم استخراج بيانات: ${data.name_ar || data.name_en}`
          : `Extracted data for: ${data.name_en || data.name_ar}`);
      } else {
        toast.success(language === 'ar' ? 'تم رفع الملف بنجاح' : 'File uploaded successfully');
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل رفع الملف' : 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const openDetail = (app) => {
    setSelectedApplicant(app);
    setShowDetailDialog(true);
  };

  const openEdit = (app) => {
    setSelectedApplicant(app);
    setEditForm({ status: app.status, department: app.department, notes: app.notes || '' });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedApplicant) return;
    setSaving(true);
    try {
      await axios.put(`${API}/recruitment/applicants/${selectedApplicant.id}`, editForm, { withCredentials: true });
      toast.success(language === 'ar' ? 'تم التحديث بنجاح' : 'Updated successfully');
      setShowEditDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/recruitment/applicants/${id}`, { withCredentials: true });
      toast.success(language === 'ar' ? 'تم الحذف بنجاح' : 'Deleted');
      fetchData();
    } catch {
      toast.error('Error');
    }
  };

  const handleQuickAction = async (appId, action) => {
    setActionLoading(appId);
    try {
      await axios.post(`${API}/recruitment/applicants/${appId}/action`, { action, notes: '' }, { withCredentials: true });
      toast.success(language === 'ar' ? 'تم تنفيذ الإجراء' : 'Action completed');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
    setActionLoading('');
  };

  const openTimeline = async (app) => {
    setSelectedApplicant(app);
    try {
      const { data } = await axios.get(`${API}/recruitment/applicants/${app.id}/timeline`, { withCredentials: true });
      setTimeline(data);
    } catch { setTimeline([]); }
    setShowTimeline(true);
  };

  const openOfferDialog = (app) => {
    setSelectedApplicant(app);
    setOfferForm({
      basic_salary: '', housing_allowance: '', transport_allowance: '', other_allowances: '',
      job_title: app.profession || '', medical_care: 'تأمين صحي بعد فترة التجربة', annual_leave_days: 30,
      probation_days: 90, offer_validity_days: 7, work_location: 'الرياض', expected_start_date: ''
    });
    setShowOfferDialog(true);
  };

  const handleGenerateOffer = async () => {
    if (!selectedApplicant || !offerForm.basic_salary) {
      toast.error(language === 'ar' ? 'يرجى إدخال الراتب الأساسي' : 'Please enter basic salary');
      return;
    }
    setGeneratingOffer(true);
    try {
      const response = await axios.post(
        `${API}/recruitment/applicants/${selectedApplicant.id}/generate-offer`,
        {
          basic_salary: parseFloat(offerForm.basic_salary) || 0,
          housing_allowance: parseFloat(offerForm.housing_allowance) || 0,
          transport_allowance: parseFloat(offerForm.transport_allowance) || 0,
          other_allowances: parseFloat(offerForm.other_allowances) || 0,
          job_title: offerForm.job_title,
          medical_care: offerForm.medical_care,
          annual_leave_days: parseInt(offerForm.annual_leave_days) || 30,
          probation_days: parseInt(offerForm.probation_days) || 90,
          offer_validity_days: parseInt(offerForm.offer_validity_days) || 7,
          work_location: offerForm.work_location,
          expected_start_date: offerForm.expected_start_date
        },
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `job_offer_${selectedApplicant.name_ar || selectedApplicant.name_en}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'ar' ? 'تم إصدار العرض الوظيفي بنجاح' : 'Job offer generated');
      setShowOfferDialog(false);
      fetchData();
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل إصدار العرض' : 'Failed to generate offer');
    } finally {
      setGeneratingOffer(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFBF0] flex items-center justify-center">
        <SpinnerGap size={36} className="animate-spin text-[#D4A843]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF0]" dir={direction} data-testid="recruitment-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#E8D48A] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#4A3508]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-[#4A3508]">
            <Briefcase size={28} weight="duotone" className="inline-block me-2 align-middle" />
            {language === 'ar' ? 'بوابة التوظيف' : 'Recruitment Portal'}
          </h1>
          <div className="relative">
            <Button onClick={() => setShowUploadDialog(true)} disabled={uploading} className="bg-[#D4A843] hover:bg-[#B8912E] text-white gap-2" data-testid="upload-cv-btn">
              {uploading ? <SpinnerGap size={18} className="animate-spin" /> : <UploadSimple size={18} weight="bold" />}
              {language === 'ar' ? 'رفع سيرة ذاتية' : 'Upload CV'}
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="recruitment-stats">
            <StatCard label={language === 'ar' ? 'الإجمالي' : 'Total'} value={stats.total} color="bg-[#4A3508]" />
            <StatCard label={language === 'ar' ? 'جديد' : 'New'} value={stats.new} color="bg-blue-500" />
            <StatCard label={language === 'ar' ? 'قيد المراجعة' : 'Reviewing'} value={stats.reviewing} color="bg-amber-500" />
            <StatCard label={language === 'ar' ? 'مقابلات' : 'Interviews'} value={stats.interview} color="bg-purple-500" />
            <StatCard label={language === 'ar' ? 'مقبول' : 'Accepted'} value={stats.accepted} color="bg-green-500" />
            <StatCard label={language === 'ar' ? 'مرفوض' : 'Rejected'} value={stats.rejected} color="bg-red-500" />
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <MagnifyingGlass size={16} className="absolute start-3 top-1/2 -translate-y-1/2 text-[#4A3508]/40" />
            <Input className="ps-9 border-[#4A3508]/15 bg-white" placeholder={language === 'ar' ? 'بحث بالاسم أو المهنة...' : 'Search by name or profession...'} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-input" />
          </div>
          <Select value={filterDept} onValueChange={setFilterDept}>
            <SelectTrigger className="w-[180px] bg-white border-[#4A3508]/15" data-testid="filter-dept">
              <SelectValue placeholder={language === 'ar' ? 'جميع الأقسام' : 'All Departments'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === 'ar' ? 'جميع الأقسام' : 'All'}</SelectItem>
              {departments.map(d => (
                <SelectItem key={d.value} value={d.value}>{language === 'ar' ? d.label_ar : d.label_en}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px] bg-white border-[#4A3508]/15" data-testid="filter-status">
              <SelectValue placeholder={language === 'ar' ? 'جميع الحالات' : 'All Statuses'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === 'ar' ? 'الكل' : 'All'}</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <SelectItem key={k} value={k}>{language === 'ar' ? v.ar : v.en}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(filterDept || filterStatus || search) && (
            <Button variant="outline" size="sm" onClick={() => { setSearch(''); setFilterDept(''); setFilterStatus(''); }} className="border-[#4A3508]/20 text-[#4A3508]">
              {language === 'ar' ? 'مسح' : 'Clear'}
            </Button>
          )}
        </div>

        {/* Applicants Table */}
        <Card className="border-[#E8D48A]/50">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table data-testid="applicants-table">
                <TableHeader>
                  <TableRow className="border-[#E8D48A]">
                    <TableHead className="text-start text-[#4A3508] text-xs whitespace-nowrap">{language === 'ar' ? 'الاسم' : 'Name'}</TableHead>
                    <TableHead className="text-center text-[#4A3508] text-xs whitespace-nowrap">{language === 'ar' ? 'المهنة' : 'Profession'}</TableHead>
                    <TableHead className="text-center text-[#4A3508] text-xs whitespace-nowrap">{language === 'ar' ? 'الجنسية' : 'Nationality'}</TableHead>
                    <TableHead className="text-center text-[#4A3508] text-xs whitespace-nowrap">{language === 'ar' ? 'القسم' : 'Department'}</TableHead>
                    <TableHead className="text-center text-[#4A3508] text-xs whitespace-nowrap">{language === 'ar' ? 'الخبرة' : 'Exp.'}</TableHead>
                    <TableHead className="text-center text-[#4A3508] text-xs whitespace-nowrap">{language === 'ar' ? 'الحالة' : 'Status'}</TableHead>
                    <TableHead className="text-center text-[#4A3508] text-xs whitespace-nowrap">{language === 'ar' ? 'التاريخ' : 'Date'}</TableHead>
                    <TableHead className="text-center text-[#4A3508] text-xs whitespace-nowrap">{language === 'ar' ? 'الإجراءات' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applicants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-[#4A3508]/40">
                        <Briefcase size={40} className="mx-auto mb-3 opacity-30" />
                        <p>{language === 'ar' ? 'لا توجد سير ذاتية. ارفع أول سيرة ذاتية!' : 'No CVs yet. Upload the first CV!'}</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    applicants.map(app => (
                      <TableRow key={app.id} className="border-[#E8D48A]/30" data-testid={`applicant-row-${app.id}`}>
                        <TableCell className="text-start">
                          <p className="font-semibold text-[#4A3508] text-sm">{app.name_ar || app.name_en || '-'}</p>
                          {app.name_en && app.name_ar && <p className="text-xs text-gray-400">{app.name_en}</p>}
                        </TableCell>
                        <TableCell className="text-center text-sm text-[#4A3508]/70">{app.profession || '-'}</TableCell>
                        <TableCell className="text-center text-sm text-[#4A3508]/70">{app.nationality || '-'}</TableCell>
                        <TableCell className="text-center">
                          <span className="text-xs bg-[#FDF8EC] text-[#4A3508] px-2 py-0.5 rounded-full border border-[#E8D48A]/50">{getDeptLabel(app.department)}</span>
                        </TableCell>
                        <TableCell className="text-center text-sm text-[#4A3508]/70">{app.years_of_experience || 0}</TableCell>
                        <TableCell className="text-center">
                          <Badge className={`${STATUS_CONFIG[app.status]?.color || 'bg-gray-100'} text-[10px]`}>
                            {language === 'ar' ? STATUS_CONFIG[app.status]?.ar : STATUS_CONFIG[app.status]?.en}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center text-[10px] text-[#4A3508]/50 whitespace-nowrap">
                          {app.created_at ? new Date(app.created_at).toLocaleDateString('ar-SA', { timeZone: 'Asia/Riyadh' }) : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex gap-1 justify-center flex-wrap">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-[#4A3508] hover:bg-[#FDF8EC]" onClick={() => openDetail(app)} data-testid={`view-${app.id}`}>
                              <Eye size={15} />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-[#4A3508] hover:bg-[#FDF8EC]" onClick={() => openTimeline(app)} data-testid={`timeline-${app.id}`} title={language === 'ar' ? 'سجل التتبع' : 'Timeline'}>
                              <Clock size={15} />
                            </Button>
                            {NEXT_ACTION_MAP[app.status] && NEXT_ACTION_MAP[app.status].action && (
                              <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700 text-white text-[10px] px-2"
                                disabled={actionLoading === app.id}
                                onClick={() => handleQuickAction(app.id, NEXT_ACTION_MAP[app.status].action)} data-testid={`action-${app.id}`}>
                                {actionLoading === app.id ? <SpinnerGap size={12} className="animate-spin" /> : <CheckCircle size={12} className="me-1" />}
                                {language === 'ar' ? NEXT_ACTION_MAP[app.status].ar : NEXT_ACTION_MAP[app.status].en}
                              </Button>
                            )}
                            {app.status === 'gm_approved' && (
                              <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700 text-white text-[10px] px-2"
                                onClick={() => openOfferDialog(app)} data-testid={`offer-${app.id}`}>
                                <FileText size={12} className="me-1" />
                                {language === 'ar' ? 'إصدار عرض' : 'Offer'}
                              </Button>
                            )}
                            {!['accepted', 'rejected'].includes(app.status) && (
                              <Button size="sm" variant="outline" className="h-7 text-red-500 border-red-200 text-[10px] px-2"
                                disabled={actionLoading === app.id}
                                onClick={() => handleQuickAction(app.id, 'reject')} data-testid={`reject-${app.id}`}>
                                <XCircle size={12} className="me-1" /> {language === 'ar' ? 'رفض' : 'Reject'}
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-[#4A3508] hover:bg-[#FDF8EC]" onClick={() => openEdit(app)} data-testid={`edit-${app.id}`}>
                              <PencilSimple size={15} />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:bg-red-50" onClick={() => handleDelete(app.id)} data-testid={`delete-${app.id}`}>
                              <Trash size={15} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-[420px]" dir={direction} data-testid="upload-dialog">
          <div dir={direction} style={{ direction }}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#4A3508]">
                <UploadSimple size={22} weight="duotone" />
                {language === 'ar' ? 'رفع سيرة ذاتية' : 'Upload CV'}
              </DialogTitle>
              <DialogDescription>
                {language === 'ar' ? 'ارفع ملف PDF وسيتم استخراج البيانات تلقائياً بالذكاء الاصطناعي' : 'Upload a PDF and data will be extracted automatically with AI'}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-[#E8D48A] rounded-xl cursor-pointer bg-[#FFFBF0] hover:bg-[#FDF8EC] transition-colors">
                <UploadSimple size={36} className="text-[#D4A843] mb-3" />
                <p className="text-sm text-[#4A3508]/70">{language === 'ar' ? 'اضغط لاختيار ملف PDF' : 'Click to select a PDF file'}</p>
                <p className="text-xs text-[#4A3508]/40 mt-1">PDF, DOC, DOCX (max 10MB)</p>
                <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleUpload} data-testid="file-input" />
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-[550px]" dir={direction} data-testid="detail-dialog">
          {selectedApplicant && (
            <div dir={direction} style={{ direction }}>
              <DialogHeader>
                <DialogTitle className="text-[#4A3508]">
                  {selectedApplicant.name_ar || selectedApplicant.name_en}
                </DialogTitle>
                <DialogDescription>{selectedApplicant.profession}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 mt-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <InfoItem label={language === 'ar' ? 'الاسم بالعربية' : 'Arabic Name'} value={selectedApplicant.name_ar} />
                  <InfoItem label={language === 'ar' ? 'الاسم بالإنجليزية' : 'English Name'} value={selectedApplicant.name_en} />
                  <InfoItem label={language === 'ar' ? 'البريد' : 'Email'} value={selectedApplicant.email} />
                  <InfoItem label={language === 'ar' ? 'الجوال' : 'Phone'} value={selectedApplicant.phone} />
                  <InfoItem label={language === 'ar' ? 'الجنسية' : 'Nationality'} value={selectedApplicant.nationality} />
                  <InfoItem label={language === 'ar' ? 'المهنة' : 'Profession'} value={selectedApplicant.profession} />
                  <InfoItem label={language === 'ar' ? 'سنوات الخبرة' : 'Experience'} value={`${selectedApplicant.years_of_experience || 0}`} />
                  <InfoItem label={language === 'ar' ? 'المؤهل' : 'Education'} value={selectedApplicant.education} />
                  <InfoItem label={language === 'ar' ? 'القسم' : 'Department'} value={getDeptLabel(selectedApplicant.department)} />
                  <InfoItem label={language === 'ar' ? 'الحالة' : 'Status'} value={language === 'ar' ? STATUS_CONFIG[selectedApplicant.status]?.ar : STATUS_CONFIG[selectedApplicant.status]?.en} />
                </div>
                {selectedApplicant.summary && (
                  <div className="bg-[#FDF8EC] rounded-lg p-3">
                    <p className="text-xs font-semibold text-[#4A3508]/60 mb-1">{language === 'ar' ? 'الملخص' : 'Summary'}</p>
                    <p className="text-sm text-[#4A3508]">{selectedApplicant.summary}</p>
                  </div>
                )}
                {selectedApplicant.certifications?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-[#4A3508]/60 mb-1">{language === 'ar' ? 'الشهادات' : 'Certifications'}</p>
                    <div className="flex flex-wrap gap-1">{selectedApplicant.certifications.map((c, i) => <Badge key={i} variant="outline" className="text-xs border-[#E8D48A] text-[#4A3508]">{c}</Badge>)}</div>
                  </div>
                )}
                {selectedApplicant.languages?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-[#4A3508]/60 mb-1">{language === 'ar' ? 'اللغات' : 'Languages'}</p>
                    <div className="flex flex-wrap gap-1">{selectedApplicant.languages.map((l, i) => <Badge key={i} variant="outline" className="text-xs border-[#E8D48A] text-[#4A3508]">{l}</Badge>)}</div>
                  </div>
                )}
                {selectedApplicant.file_path && (
                  <Button variant="outline" className="w-full border-[#E8D48A] text-[#4A3508] gap-2" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${selectedApplicant.file_path}`, '_blank')} data-testid="view-cv-btn">
                    <FileText size={16} />
                    {language === 'ar' ? 'عرض السيرة الذاتية' : 'View CV'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[420px]" dir={direction} data-testid="edit-dialog">
          {selectedApplicant && (
            <div dir={direction} style={{ direction }}>
              <DialogHeader>
                <DialogTitle className="text-[#4A3508]">
                  {language === 'ar' ? 'تعديل حالة المتقدم' : 'Update Applicant'}
                </DialogTitle>
                <DialogDescription>{selectedApplicant.name_ar || selectedApplicant.name_en}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>{language === 'ar' ? 'الحالة' : 'Status'}</Label>
                  <Select value={editForm.status} onValueChange={v => setEditForm(p => ({ ...p, status: v }))}>
                    <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{language === 'ar' ? v.ar : v.en}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'ar' ? 'القسم' : 'Department'}</Label>
                  <Select value={editForm.department} onValueChange={v => setEditForm(p => ({ ...p, department: v }))}>
                    <SelectTrigger data-testid="edit-dept"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {departments.map(d => (
                        <SelectItem key={d.value} value={d.value}>{language === 'ar' ? d.label_ar : d.label_en}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'ar' ? 'ملاحظات' : 'Notes'}</Label>
                  <Textarea value={editForm.notes} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} className="min-h-[80px]" data-testid="edit-notes" />
                </div>
              </div>
              <DialogFooter className="mt-4">
                <Button onClick={handleSaveEdit} disabled={saving} className="bg-[#D4A843] hover:bg-[#B8912E]" data-testid="save-edit-btn">
                  {saving ? <SpinnerGap size={18} className="animate-spin" /> : (language === 'ar' ? 'حفظ' : 'Save')}
                </Button>
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Job Offer Dialog */}
      <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
        <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto" dir={direction} data-testid="offer-dialog">
          {selectedApplicant && (
            <div dir={direction} style={{ direction }}>
              <DialogHeader>
                <DialogTitle className="text-[#4A3508] flex items-center gap-2">
                  <FileText size={22} weight="duotone" />
                  {language === 'ar' ? 'إصدار عرض وظيفي' : 'Generate Job Offer'}
                </DialogTitle>
                <DialogDescription>{selectedApplicant.name_ar || selectedApplicant.name_en} - {selectedApplicant.profession}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="bg-[#FDF8EC] rounded-lg p-3 text-xs text-[#4A3508]/70">
                  {language === 'ar' ? 'أدخل مفردات الراتب والتفاصيل لإصدار العرض الوظيفي PDF' : 'Enter salary details to generate the job offer PDF'}
                </div>

                <p className="text-xs font-bold text-[#4A3508]">{language === 'ar' ? 'المسمى الوظيفي' : 'Job Title'}</p>
                <div className="space-y-1">
                  <Input value={offerForm.job_title} onChange={e => setOfferForm(p => ({ ...p, job_title: e.target.value }))} placeholder={language === 'ar' ? 'المسمى الوظيفي في العرض' : 'Job title for offer'} data-testid="offer-job-title" />
                </div>

                <p className="text-xs font-bold text-[#4A3508]">{language === 'ar' ? 'مفردات الراتب' : 'Salary Breakdown'}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">{language === 'ar' ? 'الراتب الأساسي *' : 'Basic Salary *'}</Label>
                    <Input type="number" dir="ltr" className="text-center" value={offerForm.basic_salary} onChange={e => setOfferForm(p => ({ ...p, basic_salary: e.target.value }))} data-testid="offer-basic" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{language === 'ar' ? 'بدل سكن' : 'Housing'}</Label>
                    <Input type="number" dir="ltr" className="text-center" value={offerForm.housing_allowance} onChange={e => setOfferForm(p => ({ ...p, housing_allowance: e.target.value }))} data-testid="offer-housing" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{language === 'ar' ? 'بدل نقل' : 'Transport'}</Label>
                    <Input type="number" dir="ltr" className="text-center" value={offerForm.transport_allowance} onChange={e => setOfferForm(p => ({ ...p, transport_allowance: e.target.value }))} data-testid="offer-transport" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{language === 'ar' ? 'بدلات أخرى' : 'Other'}</Label>
                    <Input type="number" dir="ltr" className="text-center" value={offerForm.other_allowances} onChange={e => setOfferForm(p => ({ ...p, other_allowances: e.target.value }))} data-testid="offer-other" />
                  </div>
                </div>

                {(offerForm.basic_salary) && (
                  <div className="bg-green-50 rounded-lg p-2 text-center">
                    <span className="text-xs text-green-600">{language === 'ar' ? 'الإجمالي:' : 'Total:'} </span>
                    <span className="font-bold text-green-700">
                      {((parseFloat(offerForm.basic_salary)||0) + (parseFloat(offerForm.housing_allowance)||0) + (parseFloat(offerForm.transport_allowance)||0) + (parseFloat(offerForm.other_allowances)||0)).toLocaleString()} {language === 'ar' ? 'ر.س' : 'SAR'}
                    </span>
                  </div>
                )}

                <p className="text-xs font-bold text-[#4A3508]">{language === 'ar' ? 'تفاصيل أخرى' : 'Other Details'}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">{language === 'ar' ? 'موقع العمل' : 'Location'}</Label>
                    <Input value={offerForm.work_location} onChange={e => setOfferForm(p => ({ ...p, work_location: e.target.value }))} data-testid="offer-location" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{language === 'ar' ? 'تاريخ المباشرة' : 'Start Date'}</Label>
                    <Input type="date" dir="ltr" value={offerForm.expected_start_date} onChange={e => setOfferForm(p => ({ ...p, expected_start_date: e.target.value }))} data-testid="offer-start-date" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{language === 'ar' ? 'فترة التجربة (يوم)' : 'Probation (days)'}</Label>
                    <Input type="number" dir="ltr" className="text-center" value={offerForm.probation_days} onChange={e => setOfferForm(p => ({ ...p, probation_days: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{language === 'ar' ? 'الإجازة السنوية (يوم)' : 'Annual Leave (days)'}</Label>
                    <Input type="number" dir="ltr" className="text-center" value={offerForm.annual_leave_days} onChange={e => setOfferForm(p => ({ ...p, annual_leave_days: e.target.value }))} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{language === 'ar' ? 'الرعاية الطبية' : 'Medical Care'}</Label>
                  <Input value={offerForm.medical_care} onChange={e => setOfferForm(p => ({ ...p, medical_care: e.target.value }))} />
                </div>
              </div>
              <DialogFooter className="mt-4">
                <Button onClick={handleGenerateOffer} disabled={generatingOffer} className="bg-green-600 hover:bg-green-700 text-white gap-2" data-testid="generate-offer-btn">
                  {generatingOffer ? <SpinnerGap size={18} className="animate-spin" /> : <FileText size={18} />}
                  {language === 'ar' ? 'إصدار العرض الوظيفي PDF' : 'Generate Offer PDF'}
                </Button>
                <Button variant="outline" onClick={() => setShowOfferDialog(false)}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Timeline Dialog */}
      <Dialog open={showTimeline} onOpenChange={setShowTimeline}>
        <DialogContent className="sm:max-w-[480px]" dir={direction} data-testid="timeline-dialog">
          {selectedApplicant && (
            <div dir={direction} style={{ direction }}>
              <DialogHeader>
                <DialogTitle className="text-[#4A3508]">{language === 'ar' ? 'سجل التتبع' : 'Timeline'}</DialogTitle>
                <DialogDescription>{selectedApplicant.name_ar || selectedApplicant.name_en}</DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-3 max-h-[400px] overflow-y-auto">
                {timeline.length === 0 ? (
                  <p className="text-center text-[#4A3508]/40 py-8">{language === 'ar' ? 'لا يوجد سجل بعد' : 'No timeline yet'}</p>
                ) : timeline.map((t, i) => (
                  <div key={i} className="flex gap-3 items-start border-b border-[#E8D48A]/30 pb-3 last:border-0">
                    <div className="w-8 h-8 rounded-full bg-[#FDF8EC] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Clock size={14} className="text-[#D4A843]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-[#4A3508]">{language === 'ar' ? t.action_label_ar : t.action_label_en}</p>
                      <p className="text-xs text-[#4A3508]/50">{t.performer_name} ({t.performer_role}) - {new Date(t.created_at).toLocaleString('ar-SA', { timeZone: 'Asia/Riyadh' })}</p>
                      {t.notes && <p className="text-xs text-[#4A3508]/70 mt-1 bg-[#FDF8EC] rounded px-2 py-1">{t.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className={`${color} rounded-xl p-3 text-white text-center`}>
      <p className="text-xs opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-0.5">{value || 0}</p>
    </div>
  );
}

function InfoItem({ label, value }) {
  return (
    <div>
      <p className="text-xs text-[#4A3508]/50">{label}</p>
      <p className="text-sm font-medium text-[#4A3508]">{value || '-'}</p>
    </div>
  );
}
